import { ActivatedRoute, Router } from '@angular/router';
import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { AppSaveBarService } from '../../../shared/save-bar/save-bar.service';
import { AppSchoolsServices } from '../schools.service';
import { AppToastManagerService } from 'src/app/shared/services/toast-manager.service';
import { BaseComponent } from '../../../shared/base-classes/base.component';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { IFormError } from '../../../shared/validation/form-error.interface';
import { plainToInstance } from 'class-transformer';
import { RESPONSIVE_GRID_PRESETS } from '../../../shared/responsive-grid-columns';
import { firstValueFrom, Subscription } from 'rxjs';
import {
  dtoToFormGroup,
  formCanSave,
  formsTouched,
  mapApiErrorResponse,
  validateAndGetValue
} from '../../../shared/validation/validation.util';
import {
  DateUtil,
  IDisplayData,
  PrivilegeEnum,
  SchoolCreateDto,
  SchoolDeactivationReasonEnum,
  SchoolDetailResponseDto,
  SchoolResponseDto,
  SchoolSettings,
  SchoolUpdateDto,
  StateEnum,
  TimezoneDict,
  UserTypeEnum
} from '@whetstoneeducation/hero-common';
import { AppPageHeaderService } from '../../../shared/page-header/page-header.service';
import { HeaderButtonAction } from '../../../shared/page-header/header-button';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { AppConfirmationDialogComponent } from '../../../shared/confirmation-dialog/confirmation-dialog.component';
import { DEFAULT_DIALOG_RESULT } from '../../../shared/confirmation-dialog/confirmation-dialog-options.interface';
import { AppSubscriptionCreateEditComponent } from './subscription-create-edit/subscription-create-edit.component';
import { AppBackButtonModalComponent } from '../../../shared/back-button-modal/back-button-modal.component';
import { AppDeactivateRecordsModalComponent } from './deactivate-records-modal/deactivate-records-modal.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { AppNavbarService } from '../../../shared/navbar/navbar.service';
import { AppUsersServices } from '../../users/users.service';

@Component({
  styleUrls: ['./school-create-edit.scss'],
  selector: 'app-school-create-edit',
  templateUrl: './school-create-edit.template.html'
})
export class AppSchoolCreateEditComponent
  extends BaseComponent
  implements AfterViewInit, OnDestroy
{
  private readonly CREATE_OPTIONS = {
    enableImplicitConversion: true,
    exposeUnsetFields: true
  };
  private readonly UPDATE_OPTIONS = {
    enableImplicitConversion: true,
    exposeUnsetFields: true
  };
  public countryOptions: IDisplayData[] = [
    { value: 'US', display: 'United States' }
  ];
  public errors: IFormError[] = [];
  public school: SchoolCreateDto | SchoolUpdateDto;
  public schoolForm: FormGroup;
  public settingsForm: FormGroup;
  public availableSchoolGroups: IDisplayData[] = [];
  public pageHeaderSubscription: Subscription;
  public stateOptions: IDisplayData[] = Object.values(StateEnum).map(
    (value) => ({
      value,
      display: value
    })
  );
  public valueChangesSubscriptions: Subscription[] = [];
  public isCreating: boolean;
  public schoolTerms: any[] = [];
  public isLoading: boolean = false;
  public timezoneOptions: { value: string; label: string }[] = [];
  public purgeDateControl = new FormControl<Date | null>(null);
  public minPurgeDate = this.getMinPurgeDate();
  public weekdayFilter = (date: Date | null): boolean => {
    if (!date) return false;
    const day = date.getDay();
    return day !== 0 && day !== 6;
  };
  public deactivationReasonControl = new FormControl<string | null>(null);
  public deactivationReasonOptions: IDisplayData[] = Object.values(
    SchoolDeactivationReasonEnum
  ).map((value) => ({
    value,
    display: value
  }));

  constructor(
    private formBuilder: FormBuilder,
    private toastService: AppToastManagerService,
    public route: ActivatedRoute,
    public router: Router,
    public saveBar: AppSaveBarService,
    public schoolsServices: AppSchoolsServices,
    public pageHeaderService: AppPageHeaderService,
    public dialog: MatDialog,
    public navbarService: AppNavbarService,
    public userService: AppUsersServices
  ) {
    super({ route, router });
    this.setupHeaderButtonSubscriptions();
  }

  private async checkDeactivatedSchool() {
    if (
      this.school instanceof SchoolUpdateDto &&
      this.school.deactivatedAt &&
      !this.isUserType([UserTypeEnum.INTERNAL_ADMIN])
    ) {
      await this.router.navigate(['schools']);
    }
  }

  async ngAfterViewInit(): Promise<void> {
    this.isLoading = true;
    this.timezoneOptions = [
      { value: null, label: '' },
      ...Object.values(TimezoneDict)
    ];
    await this.loadData(this.route.snapshot.data.data);
    this.checkDeactivatedSchool();

    const exclude = this.isUpdateSchool() ? ['schoolGroupId'] : [];
    exclude.push('settings', 'deactivatedAt', 'churnDate', 'deactivationReason');
    this.syncPurgeDateControl();
    this.schoolForm = dtoToFormGroup(this.school, this.formBuilder, {
      exclude: exclude
    });

    this.settingsForm = dtoToFormGroup(this.school.settings, this.formBuilder);
    this.settingsForm
      .get('email_guardian_invite')
      .setValue(this.school.settings.email_guardian_invite === '1');
    this.settingsForm
      .get('incident')
      .setValue(this.school.settings.incident === '1');
    this.settingsForm.get('event').setValue(this.school.settings.event === '1');

    this.valueChangesSubscriptions.push(
      this.schoolForm.valueChanges.subscribe(async () => {
        if (this.school instanceof SchoolUpdateDto) {
          const formSchool = await validateAndGetValue<SchoolUpdateDto>(
            this.schoolForm,
            SchoolUpdateDto
          );
          if (!(this.school instanceof SchoolUpdateDto)) return;
          const { deactivatedAt, churnDate, deactivationReason, settings } =
            this.school;
          Object.assign(this.school, formSchool, {
            deactivatedAt,
            churnDate,
            deactivationReason,
            settings
          });
          this.schoolTerms = await this.schoolsServices.getSchoolTerms(
            this.school.id
          );
        } else {
          this.school = await validateAndGetValue<SchoolCreateDto>(
            this.schoolForm,
            SchoolCreateDto
          );
        }
        this.school.settings = await validateAndGetValue<SchoolSettings>(
          this.settingsForm,
          SchoolSettings
        );
      })
    );

    this.valueChangesSubscriptions.push(
      this.settingsForm.valueChanges.subscribe(async () => {
        this.school.settings = await validateAndGetValue<SchoolSettings>(
          this.settingsForm,
          SchoolSettings
        );
      })
    );
    this.isLoading = false;
  }

  ngOnDestroy() {
    this.saveBar.hide();
    this.pageHeaderSubscription.unsubscribe();
    this.valueChangesSubscriptions.forEach((subscription) =>
      subscription.unsubscribe()
    );
  }

  public get responsiveGridPresets() {
    return RESPONSIVE_GRID_PRESETS.TWO_COLUMN;
  }

  public setupHeaderButtonSubscriptions() {
    this.pageHeaderSubscription =
      this.pageHeaderService.buttonAction$.subscribe(
        async (action: HeaderButtonAction) => {
          if (
            action === HeaderButtonAction.SAVE &&
            formsTouched([this.schoolForm, this.settingsForm], this.toastService)
          ) {
            if (formCanSave(this.schoolForm, this.toastService, false)) {
              const school = await this.saveSchool(this.school);

              if (school && school.id) {
                const user = this.StorageManager.getLoggedInUser();
                const schoolGroup = user.schoolGroups.find(
                  (group) => group.id === school.schoolGroupId
                );
                if (schoolGroup) {
                  const schoolIndex = schoolGroup.schools.findIndex(
                    (school) => school.id === school.id
                  );
                  if (schoolIndex > -1) {
                    schoolGroup.schools[schoolIndex] = {
                      id: school.id,
                      name: school.name
                    };
                  } else {
                    schoolGroup.schools.push({
                      id: school.id,
                      name: school.name
                    });
                  }
                }
                this.StorageManager.saveUserInfo(user);

                await this.userService.updateSettings();

                this.navbarService.updateData();

                await this.router.navigate(['schools']);
              }
            }
          }

          if (
            action === HeaderButtonAction.EDIT_SUBSCRIPTION &&
            this.isCreating === false
          ) {
            this.dialog.open(AppSubscriptionCreateEditComponent, {
              data: {
                schoolId: this.route.snapshot.data.data.id,
                onClose: async () => {
                  this.dialog.closeAll();
                }
              },
              width: '500px',
              height: 'auto',
              autoFocus: false
            });
          }

          if (action === HeaderButtonAction.BACK) {
            if (formsTouched([this.schoolForm, this.settingsForm])) {
              // show dialog asking you sure, things will not be saved
              // AppBackButtonModalComponent
              this.dialog.open(AppBackButtonModalComponent, {
                data: {
                  navigateTo: '/schools',
                  onClose: async () => {
                    this.dialog.closeAll();
                  }
                },
                width: '500px',
                height: 'auto',
                autoFocus: false
              });
            } else {
              // just go back to school list page
              await this.router.navigate(['/schools']);
            }
          }

          if (action === HeaderButtonAction.DEACTIVATE_RECORDS) {
            this.dialog.open(AppDeactivateRecordsModalComponent, {
              width: '500px',
              autoFocus: true,
              data: {
                schoolId: this.route.snapshot.data.data.id
              }
            });
          }
        }
      );
  }

  public onBooleanSettingToggle(setting: string, event: MatCheckboxChange) {
    this.school.settings[setting] = event.checked ? '1' : '0';
    this.schoolForm.markAsTouched();
  }

  public isUpdateSchool(): boolean {
    return this.school instanceof SchoolUpdateDto;
  }

  public get schoolUpdate(): SchoolUpdateDto | null {
    return this.school instanceof SchoolUpdateDto ? this.school : null;
  }

  public get isSchoolDeactivated(): boolean {
    return !!this.schoolUpdate?.deactivatedAt;
  }

  public toggleDeactivateSchool(): void {
    const school = this.schoolUpdate;
    if (!school) return;

    if (school.deactivatedAt) {
      this.toggleAndStay();
      return;
    }

    this.deactivationReasonControl.reset();
    this.dialog.open(AppConfirmationDialogComponent, {
      width: '40rem',
      height: 'auto',
      autoFocus: false,
      data: {
        title: 'School Deactivation',
        content: `Are you sure you want to deactivate ${school.name}?\n\nSchool users will lose access to Hero. All settings and data will remain intact, and the school can be reactivated at any time.`,
        select: {
          label: 'Reason for Deactivation',
          options: this.deactivationReasonOptions,
          control: this.deactivationReasonControl
        },
        actions: [
          {
            key: 'cancel',
            label: 'Cancel',
            color: 'secondary',
            classes: 'outline-button'
          },
          {
            key: 'deactivate',
            label: 'Yes, Deactivate',
            color: 'primary',
            classes: 'modal-delete-button',
            awaitAction: true,
            action: async () => {
              if (!this.deactivationReasonControl.value) return false;
              await this.toggleAndStay(
                this.deactivationReasonControl
                  .value as SchoolDeactivationReasonEnum
              );
            }
          }
        ]
      }
    });
  }

  public async onPurgeDateChange(
    event: MatDatepickerInputEvent<Date>
  ): Promise<void> {
    const school = this.schoolUpdate;
    if (!school?.deactivatedAt) return;

    const selectedDate = event.value;
    this.syncPurgeDateControl();

    if (!selectedDate) {
      await this.savePurgeDate(null);
      return;
    }

    const dialogRef = this.dialog.open(AppConfirmationDialogComponent, {
      width: '40rem',
      height: 'auto',
      autoFocus: false,
      data: {
        title: 'Confirm Data Purge Date',
        content:
          'Purging data is irreversible. Please confirm the account name and status before continuing.',
        details: [
          { label: 'Account', value: school.name },
          { label: 'Status', value: 'Deactivated' },
          {
            label: 'Purge date',
            value: DateUtil.convertTimestampToReadableDate(selectedDate.getTime())
          }
        ],
        typeToConfirm: 'PURGE DATA',
        confirmLabel: 'Confirm Purge Date',
        confirmClasses: 'modal-delete-button',
        cancelClasses: 'outline-button'
      }
    });
    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result !== DEFAULT_DIALOG_RESULT.YES) {
      if (result === DEFAULT_DIALOG_RESULT.FAILED_CONFIRMATION) {
        this.toastService.error('Confirmation failed, purge date not saved.');
      }
      return;
    }

    await this.savePurgeDate(selectedDate);
  }

  private async savePurgeDate(churnDate: Date | null): Promise<void> {
    const school = this.schoolUpdate;
    if (!school) return;

    try {
      const result = await this.schoolsServices.setSchoolChurnDate(
        school.id,
        churnDate
      );
      school.churnDate = result.churnDate;
      this.syncPurgeDateControl();
    } catch (errorResponse) {
      this.toastService.error(
        errorResponse?.error?.message || 'Error saving purge date'
      );
      this.syncPurgeDateControl();
    }
  }

  private async toggleAndStay(
    deactivationReason?: SchoolDeactivationReasonEnum
  ): Promise<void> {
    const school = this.schoolUpdate;
    if (!school) return;

    const result = await this.schoolsServices.deactivateSchool(
      school.id,
      deactivationReason
    );
    if (!result) {
      await this.router.navigate(['login']);
      return;
    }

    school.deactivatedAt = result.deactivatedAt;
    school.churnDate = result.churnDate;
    school.deactivationReason = result.deactivationReason;
    this.syncPurgeDateControl();
  }

  private syncPurgeDateControl(): void {
    const churnDate = this.schoolUpdate?.churnDate;
    // Same adjustment used for other date-only fields so the picker shows
    // the stored calendar day.
    const value = churnDate
      ? DateUtil.adjustDateToUtcTimezone(new Date(churnDate))
      : null;
    this.purgeDateControl.setValue(value, {
      emitEvent: false
    });
    if (this.isSchoolDeactivated) {
      this.purgeDateControl.enable({ emitEvent: false });
    } else {
      this.purgeDateControl.disable({ emitEvent: false });
    }
  }

  private getMinPurgeDate(): Date {
    const minDate = new Date();
    minDate.setHours(0, 0, 0, 0);
    minDate.setDate(minDate.getDate() + 5);
    return minDate;
  }

  public async loadData(data: SchoolResponseDto) {
    this.availableSchoolGroups =
      this.StorageManager.getLoggedInUser().schoolGroups.map((group) => {
        return { value: group.id, display: group.name };
      });
    const school = data;
    try {
      if (!school.id) {
        this.school = plainToInstance(
          SchoolCreateDto,
          school,
          this.CREATE_OPTIONS
        );
        this.isCreating = true;
        this.school.settings = new SchoolSettings();
      } else {
        this.schoolTerms = await this.schoolsServices.getSchoolTerms(school.id);
        this.school = new SchoolUpdateDto().mapFields(school);
        this.isCreating = false;
      }
    } catch (error) {
      this.toastService.error('An error occurred while to loading the school!');
    }
  }

  public async saveSchool(
    school: SchoolCreateDto | SchoolUpdateDto
  ): Promise<SchoolDetailResponseDto> {
    try {
      this.isLoading = true;
      let schoolResult: SchoolDetailResponseDto;
      if (school instanceof SchoolCreateDto) {
        schoolResult = await this.schoolsServices.createSchool(school);
        this.school = new SchoolUpdateDto(schoolResult).mapFields(school);
        this.toastService.success('School created successfully!');
        this.isCreating = false;
      } else {
        schoolResult = await this.schoolsServices.updateSchool(school);
        this.school = new SchoolUpdateDto(schoolResult).mapFields(school);
        this.school.deactivatedAt = schoolResult.deactivatedAt;
        this.school.churnDate = schoolResult.churnDate;
        this.school.deactivationReason = schoolResult.deactivationReason;
        this.syncPurgeDateControl();
        this.toastService.success('School saved successfully!');
      }
      return schoolResult;
    } catch (errorResponse) {
      this.errors = mapApiErrorResponse(errorResponse);
      this.toastService.error(
        'An error occurred when attempting to save your school!'
      );
    }
    this.isLoading = false;
  }

  protected readonly PrivilegeEnum = PrivilegeEnum;
  protected readonly UserTypeEnum = UserTypeEnum;
}
